import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ApiError,
  myTeamsApi,
  type DirectoryPlayer,
  type LineupCellRef,
  type LineupWire,
  type MyTeamsReference,
  type MyTeamsSport,
} from '../api-client.js';

/**
 * The lineup builder for one (sport, format, era) cell.
 *
 * Select a slot on the left, pick a player on the right. Every placement
 * is a single autosave write — there is no explicit save button, and the
 * first placement is what creates the lineup server-side.
 *
 * The server's response is the source of truth after every write: the API
 * returns the whole lineup, so the UI replaces its state with what was
 * actually stored rather than patching optimistically and drifting. A
 * rejection (out-of-era pick, a second captain) leaves the previous state
 * untouched and surfaces the server's message verbatim, because those
 * messages are written to be acted on.
 */
export default function MyTeamsBuilderPage() {
  const params = useParams();
  const cell: LineupCellRef = {
    sport: params.sport ?? '',
    format: params.format ?? '',
    era: params.era ?? '',
  };

  const [reference, setReference] = useState<MyTeamsReference | null>(null);
  const [lineup, setLineup] = useState<LineupWire | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<number>(1);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<DirectoryPlayer[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  const cellKey = `${cell.sport}/${cell.format}/${cell.era}`;

  useEffect(() => {
    let cancelled = false;
    setError(null);
    Promise.all([myTeamsApi.reference(), myTeamsApi.getLineup(cell)])
      .then(([ref, saved]) => {
        if (cancelled) return;
        setReference(ref);
        setLineup(saved);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
    // The cell is the identity of this page; re-fetch when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellKey]);

  // Debounced directory search. The era is part of the query, so the
  // picker only ever offers players the API would actually accept — the
  // server still re-checks, but the user should not meet a 422 for a
  // player the picker showed them.
  useEffect(() => {
    if (!lineup) return;
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      myTeamsApi
        .searchPlayers({ sport: cell.sport, era: cell.era, q: search.trim() || undefined, limit: 40 })
        .then((page) => !cancelled && setResults(page.players))
        .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
        .finally(() => !cancelled && setSearching(false));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, cellKey, lineup !== null]);

  const sport: MyTeamsSport | null = useMemo(
    () => reference?.sports.find((s) => s.slug === cell.sport) ?? null,
    [reference, cell.sport],
  );

  const slotsByNumber = useMemo(() => {
    const map = new Map<number, LineupWire['slots'][number]>();
    for (const slot of lineup?.slots ?? []) map.set(slot.slotNumber, slot);
    return map;
  }, [lineup]);

  /** Every write funnels through here so errors are reported identically. */
  const run = useCallback(async (action: () => Promise<LineupWire>, success?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setLineup(await action());
      if (success) setNotice(success);
    } catch (e) {
      // The API's messages name the slot, the player and the reason —
      // show them as-is rather than replacing them with a generic one.
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, []);

  if (error && !lineup) {
    return (
      <div className="ec-surface m-4 p-4 text-sm">
        <p className="font-display uppercase tracking-wide text-ec-face">Cannot open this lineup</p>
        <p className="mt-2 text-ec-bone-dim">{error}</p>
        <Link to="/my-teams" className="mt-4 inline-block text-sm text-ec-brass hover:underline">
          Back to My Teams
        </Link>
      </div>
    );
  }

  if (!lineup || !sport) return <p className="p-4 text-sm text-ec-bone-dim">Loading lineup…</p>;

  const squadSize = lineup.format.squadSize;
  const slotNumbers = Array.from({ length: squadSize }, (_, i) => i + 1);
  const selected = slotsByNumber.get(selectedSlot) ?? null;
  const placedPlayerIds = new Set((lineup.slots ?? []).map((slot) => slot.player.id));

  return (
    <div className="flex flex-col gap-4 p-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <Link to="/my-teams" className="text-sm text-ec-brass hover:underline">
            ← My Teams
          </Link>
          <h1 className="font-display text-lg font-medium uppercase tracking-wide text-ec-face">
            {lineup.sport.name} · {lineup.format.name} · {lineup.era.name}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-ec-bone-dim">
            {lineup.completeness.filled} / {squadSize}
          </span>
          <button
            type="button"
            disabled={busy}
            className="ec-btn ec-btn--gold !py-1.5 text-sm disabled:opacity-50"
            onClick={() =>
              run(
                () => (lineup.isPublic ? myTeamsApi.unpublish(cell) : myTeamsApi.publish(cell)),
                lineup.isPublic ? 'Unpublished.' : 'Published.',
              )
            }
          >
            {lineup.isPublic ? 'Unpublish' : 'Publish'}
          </button>
        </div>
      </header>

      {error && (
        <p className="rounded-md border border-ec-oxblood bg-[rgba(39,147,211,0.28)] p-3 text-sm text-ec-face">
          {error}
        </p>
      )}
      {notice && <p className="text-sm text-ec-brass">{notice}</p>}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ---- Slots ---- */}
        <section className="ec-surface flex flex-col gap-2 p-4">
          <h2 className="font-display text-sm uppercase tracking-wide text-ec-face">Lineup</h2>
          <ul className="flex flex-col gap-1">
            {slotNumbers.map((slotNumber) => {
              const slot = slotsByNumber.get(slotNumber);
              const isSelected = slotNumber === selectedSlot;
              // AFL benches from 19; nothing else seeded has a bench.
              const bench = squadSize === 22 && slotNumber === 19;
              return (
                <li key={slotNumber}>
                  {bench && (
                    <p className="mt-2 font-display text-[10px] uppercase tracking-wide text-ec-bone-dim">
                      Interchange
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => setSelectedSlot(slotNumber)}
                    aria-pressed={isSelected}
                    className={`flex w-full items-center gap-2 rounded-sm border px-2 py-1.5 text-left text-sm ${
                      isSelected
                        ? 'border-ec-brass bg-[rgba(245,168,28,0.12)]'
                        : 'border-ec-brass/20 hover:border-ec-brass/50'
                    }`}
                  >
                    <span className="w-6 shrink-0 font-mono text-xs text-ec-bone-dim">
                      {slotNumber}
                    </span>
                    {slot ? (
                      <>
                        <span className="flex-1 text-ec-face">
                          {slot.player.knownAs ?? slot.player.fullName}
                        </span>
                        {slot.roleFlags.captain && <RoleChip label="C" />}
                        {slot.roleFlags.viceCaptain && <RoleChip label="VC" />}
                        {slot.roleFlags.wicketKeeper && <RoleChip label="WK" />}
                        {slot.position && (
                          <span className="shrink-0 text-xs text-ec-bone-dim">
                            {slot.position.name}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="flex-1 text-ec-bone-dim">Empty</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        {/* ---- Picker + slot detail ---- */}
        <section className="flex flex-col gap-4">
          {selected && (
            <div className="ec-surface flex flex-col gap-2 p-4">
              <h2 className="font-display text-sm uppercase tracking-wide text-ec-face">
                Slot {selectedSlot} — {selected.player.knownAs ?? selected.player.fullName}
              </h2>

              <label className="flex items-center gap-2 text-sm">
                <span className="text-ec-bone-dim">Position</span>
                <select
                  className="flex-1 rounded-sm border border-ec-brass/40 bg-ec-ground px-2 py-1 text-sm text-ec-face"
                  value={selected.position?.slug ?? ''}
                  disabled={busy}
                  onChange={(event) =>
                    run(() =>
                      myTeamsApi.putSlot(cell, selectedSlot, {
                        playerId: selected.player.id,
                        positionSlug: event.target.value || null,
                        roleFlags: selected.roleFlags,
                      }),
                    )
                  }
                >
                  <option value="">—</option>
                  {sport.positions.map((position) => (
                    <option key={position.slug} value={position.slug}>
                      {position.name}
                    </option>
                  ))}
                </select>
              </label>

              <div className="flex flex-wrap gap-2">
                {(['captain', 'viceCaptain', 'wicketKeeper'] as const).map((flag) => (
                  <button
                    key={flag}
                    type="button"
                    disabled={busy}
                    className={
                      selected.roleFlags[flag]
                        ? 'ec-btn ec-btn--primary !py-1.5 text-xs'
                        : 'ec-btn !py-1.5 text-xs'
                    }
                    onClick={() => {
                      // Toggling OFF omits the key entirely — the API
                      // rejects `false`, because "clear it" and "set it
                      // false" must not be the same request.
                      const next = { ...selected.roleFlags };
                      if (next[flag]) delete next[flag];
                      else next[flag] = true;
                      return run(() =>
                        myTeamsApi.putSlot(cell, selectedSlot, {
                          playerId: selected.player.id,
                          positionSlug: selected.position?.slug ?? null,
                          roleFlags: next,
                        }),
                      );
                    }}
                  >
                    {flag === 'captain'
                      ? 'Captain'
                      : flag === 'viceCaptain'
                        ? 'Vice-captain'
                        : 'Wicket-keeper'}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={busy}
                  className="ec-btn !py-1.5 ml-auto text-xs"
                  onClick={() => run(() => myTeamsApi.clearSlot(cell, selectedSlot), 'Slot cleared.')}
                >
                  Clear slot
                </button>
              </div>
            </div>
          )}

          <div className="ec-surface flex flex-col gap-2 p-4">
            <h2 className="font-display text-sm uppercase tracking-wide text-ec-face">
              Pick for slot {selectedSlot}
            </h2>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={`Search ${lineup.sport.name} players…`}
              className="rounded-sm border border-ec-brass/40 bg-ec-ground px-2 py-1.5 text-sm text-ec-face"
            />
            <p className="text-xs text-ec-bone-dim">
              Showing players eligible for the {lineup.era.name} era.
            </p>

            {searching && <p className="text-xs text-ec-bone-dim">Searching…</p>}

            <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto">
              {(results ?? []).map((player) => {
                const alreadyPlaced = placedPlayerIds.has(player.id);
                return (
                  <li key={player.id}>
                    <button
                      type="button"
                      disabled={busy || alreadyPlaced}
                      className="flex w-full items-baseline gap-2 rounded-sm border border-ec-brass/20 px-2 py-1.5 text-left text-sm hover:border-ec-brass/50 disabled:opacity-40"
                      onClick={() =>
                        run(() =>
                          myTeamsApi.putSlot(cell, selectedSlot, { playerId: player.id }),
                        )
                      }
                    >
                      <span className="flex-1 text-ec-face">
                        {player.knownAs ?? player.fullName}
                      </span>
                      <span className="font-mono text-xs text-ec-bone-dim">
                        {player.careerStartYear ?? '?'}–{player.careerEndYear ?? (player.isActive ? '' : '?')}
                      </span>
                      {alreadyPlaced && <span className="text-xs text-ec-brass">picked</span>}
                    </button>
                  </li>
                );
              })}
              {results !== null && results.length === 0 && !searching && (
                <li className="text-xs text-ec-bone-dim">
                  No eligible players match “{search}”.
                </li>
              )}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}

function RoleChip({ label }: { label: string }) {
  return (
    <span className="shrink-0 rounded-sm border border-ec-brass/50 bg-ec-ground px-1.5 py-0.5 font-display text-[10px] uppercase tracking-wide text-ec-brass">
      {label}
    </span>
  );
}
